import { useState } from 'react';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { Box, Button, CircularProgress, Container, Stack, Typography } from '@mui/material';
import { Report, Stars } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Loader,
  Pagination,
  TitlePanels,
  ToAuditButton,
  dateTime,
  usePagination,
  useToast,
  Badge,
} from '@shared';
import { useNeedsReview } from './useNeedsReview';
import { RouteEnum, needsReviewApi } from 'App';

const columns: GridColDef[] = [
  {
    field: 'partnerName',
    headerName: 'Partner',
    width: 180,
    sortable: false,
    editable: false,
    renderCell: (params) => (
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Typography component='span' variant='tableCell'>
          {params.value}
        </Typography>
        {params.row.specialPartner && (
          <Badge title='Special partner' icon={<Stars color='primary' />} />
        )}
      </Stack>
    ),
  },
  {
    field: 'examStartTime',
    headerName: 'Exam Start Time',
    width: 140,
    editable: false,
    sortable: false,
    hideable: true,
    valueFormatter: ({ value, field }) => dateTime(value, field),
  },
  {
    field: 'examEndTime',
    headerName: 'Exam End Time',
    width: 140,
    sortable: false,
    editable: false,
    valueFormatter: ({ value, field }) => dateTime(value, field),
  },
  {
    field: 'auditor',
    headerName: 'Auditor',
    width: 160,
    sortable: false,
    editable: false,
  },
  {
    field: 'auditStart',
    headerName: 'Audit Start',
    width: 120,
    sortable: false,
    editable: false,
    valueFormatter: ({ value, field }) => dateTime(value, field),
  },
  {
    field: 'auditEnd',
    headerName: 'Audit End',
    width: 120,
    sortable: false,
    editable: false,
    valueFormatter: ({ value, field }) => dateTime(value, field),
  },
  {
    field: 'anomalyCount',
    headerName: 'Anomalies',
    sortable: false,
    editable: false,
    renderCell: ({ row }) => (
      <Stack direction='row' alignItems='center' gap={1}>
        <Typography variant='tableCell'>{row.anomalyCount}</Typography>
        {row.auditOfInterest && <Report color='error' />}
      </Stack>
    ),
  },
  {
    field: 'action',
    headerName: '',
    minWidth: 140,
    maxWidth: 600,
    sortable: false,
    editable: false,
    renderCell: ({ row }) => (
      <ToAuditButton
        uuid={row.uuid}
        secondUuid={row.auditUuid}
        title={'Audit Page'}
        route={RouteEnum.Review}
        navigate
      />
    ),
  },
];

const LIMIT = 20;

export function NeedsReview() {
  const [page, setPage] = usePagination();
  const toast = useToast();
  const [selectedAudits, setSelectedAudits] = useState<GridRowSelectionModel>([]);

  const { data, isError, isFetching, isLoading, refetch } = useNeedsReview(page);
  const queryClient = useQueryClient();
  queryClient.setQueryData(['needs review current page'], page);

  const { mutate: onFlag, isLoading: isLoadingFlaging } = useMutation({
    mutationFn: (data: string[]) => needsReviewApi.flag(data as string[]),
    onSuccess: (data) => {
      refetch();
      toast.success(`${data} sessions have been flaged as audit of interest`);
      setSelectedAudits([]);
    },
  });

  const { mutate: unMarkUnderReview, isLoading: isLoadingUnmark } = useMutation({
    mutationFn: (data: string[]) => needsReviewApi.unMarkUnderReview(data as string[]),
    onSuccess: (data) => {
      refetch();
      toast.success(`${data} sessions have been unmarked under review`);
      setSelectedAudits([]);
    },
  });

  const disableButtons = isLoadingFlaging || isLoadingUnmark || !selectedAudits.length;

  if (isLoading) {
    return <Loader />;
  }
  if (isError) {
    return null;
  }

  const { totalResults, compositeAudits } = data;

  const pagination = totalResults > compositeAudits.length;
  const totalPages = pagination ? Math.ceil(totalResults / LIMIT) : 1;

  return (
    <Container component='article'>
      <TitlePanels titles={['Review Queue']} counter={totalResults}>
        <Stack direction='row' gap={1}>
          <Button
            variant='outlined'
            disabled={disableButtons}
            onClick={() => onFlag(selectedAudits as string[])}
          >
            {isLoadingFlaging ? (
              <CircularProgress size={18} color='inherit' />
            ) : (
              'Mark as audit of interest'
            )}
          </Button>
          <Button
            variant='contained'
            disabled={disableButtons}
            onClick={() => unMarkUnderReview(selectedAudits as string[])}
          >
            {isLoadingUnmark ? (
              <CircularProgress size={18} color='inherit' />
            ) : (
              'Remove under review'
            )}
          </Button>
        </Stack>
      </TitlePanels>
      <Typography variant='body2' ml={2} mb={2}>
        Selected rows: {selectedAudits.length}
      </Typography>
      <DataGrid
        autoHeight
        getRowId={(row) => row.auditUuid}
        getRowHeight={() => 'auto'}
        columns={columns}
        rows={compositeAudits}
        checkboxSelection
        loading={isFetching}
        rowSelectionModel={selectedAudits}
        onRowSelectionModelChange={setSelectedAudits}
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableRowSelectionOnClick
        hideFooter={!totalPages}
        sx={{ '--DataGrid-overlayHeight': '52px' }}
        slots={{
          pagination: () => (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: totalPages <= 1 ? 'flex-end' : 'center',
                gap: 2,
                px: 1,
              }}
            >
              {totalPages > 1 && (
                <Pagination totalPages={totalPages} onPageChange={setPage} currentPage={page} />
              )}
            </Box>
          ),
          noRowsOverlay: () => (
            <Box sx={{ width: '100%', py: 2, px: 1, textAlign: 'center' }}>No audits found</Box>
          ),
        }}
      />
    </Container>
  );
}
